import {
  PROJECT_SIDEBAR_GROUPS, flattenSidebarItems, type ProjectPhaseKey,
} from '../../data/projectSidebarConfig';

/** Titel und Kurzbeschreibung je Menüseite (Schlüssel = Menü-Key). */
const PAGE_TEXTS: Record<string, { title: string; description: string }> = {
  dashboard: { title: 'Dashboard', description: 'Zeigt Stand, Kennzahlen und Reifegrad des Projekts auf einen Blick.' },
  stakeholders: { title: 'Stakeholder', description: 'Erfasst alle Beteiligten mit Einfluss, Interesse und Ansprechpartner.' },
  machbarkeit: { title: 'Machbarkeit (Scope)', description: 'Bewertet, ob das Vorhaben umsetzbar ist, und hält Rahmenbedingungen fest.' },
  projektauftrag: { title: 'Projektauftrag', description: 'Beschreibt Anlass, Nutzen und Auftrag des Projekts als Grundlage für alles Weitere.' },
  ziele: { title: 'Ziele', description: 'Legt messbare Projektziele und Erfolgskriterien fest.' },
  team: { title: 'Team', description: 'Benennt die Projektmitglieder mit ihren Rollen.' },
  zeiten: { title: 'Zeiten', description: 'Hält Start, Ende und Meilensteine des Projekts fest.' },
  projektplan: { title: 'Projektplan', description: 'Plant Arbeitspakete und Aufgaben zeitlich und zeigt Abhängigkeiten.' },
  anforderungen: { title: 'Anforderungen', description: 'Sammelt und priorisiert, was das Ergebnis leisten muss.' },
  scope: { title: 'Scope Statement', description: 'Grenzt ab, was zum Projekt gehört und was nicht.' },
  arbeitspakete: { title: 'Arbeitspakete', description: 'Gliedert den Scope in überschaubare Pakete mit Verantwortlichen und Aufwand.' },
  risiken: { title: 'Risiken', description: 'Erfasst mögliche Probleme, bewertet sie und plant Gegenmaßnahmen.' },
  meetings: { title: 'Meetings', description: 'Plant Termine und dokumentiert Besprechungen mit Ergebnissen.' },
  'offene-punkte': { title: 'Offene Punkte', description: 'Verfolgt ungeklärte Fragen und Aufgaben bis zur Erledigung.' },
  kanban: { title: 'Kanban', description: 'Zeigt die Aufgaben als Tafel und macht den Arbeitsfortschritt sichtbar.' },
  burnup: { title: 'Fortschritt', description: 'Stellt erledigten gegenüber geplantem Umfang im Zeitverlauf dar.' },
  status: { title: 'Status-Report', description: 'Fasst den Wochenstand für Auftraggeber und Team zusammen.' },
  retro: { title: 'Retrospektive', description: 'Sammelt, was gut lief und was künftig besser werden soll.' },
  abschluss: { title: 'Abschluss', description: 'Schließt das Projekt ab und hält Ergebnis und Lehren fest.' },
};

/** Zusammenhänge zwischen den Seiten (reine Darstellung). */
export const FLOW_EDGES: { from: string; to: string }[] = [
  { from: 'machbarkeit', to: 'projektauftrag' },
  { from: 'projektauftrag', to: 'ziele' },
  { from: 'ziele', to: 'anforderungen' },
  { from: 'anforderungen', to: 'arbeitspakete' },
  { from: 'arbeitspakete', to: 'kanban' },
  { from: 'kanban', to: 'burnup' },
  { from: 'burnup', to: 'status' },
  { from: 'status', to: 'retro' },
  { from: 'retro', to: 'abschluss' },
];

export const NODE_W = 230;
export const NODE_H = 104;
const COL_GAP = 70;
const ROW_GAP = 24;
const HEAD_H = 40;
const PAD = 20;

export interface GraphNode {
  key: string;
  title: string;
  description: string;
  group: ProjectPhaseKey;
  x: number;
  y: number;
}
export interface GraphColumn { key: ProjectPhaseKey; title: string; x: number }
export interface ProgrammGraph {
  nodes: GraphNode[];
  columns: GraphColumn[];
  width: number;
  height: number;
}

const GROUP_TITLES: Record<ProjectPhaseKey, string> = {
  overview: 'Übersicht',
  initiation: 'Initiierung',
  planning: 'Planung',
  execution: 'Ausführung',
  closure: 'Abschluss',
};

/** Leitet Knoten und Spalten aus dem Projektmenü ab (ohne die Programmübersicht selbst). */
export function buildProgrammGraph(): ProgrammGraph {
  const nodes: GraphNode[] = [];
  const columns: GraphColumn[] = [];
  let maxRows = 0;
  PROJECT_SIDEBAR_GROUPS.forEach((group, col) => {
    const x = PAD + col * (NODE_W + COL_GAP);
    columns.push({ key: group.key, title: GROUP_TITLES[group.key], x });
    const items = flattenSidebarItems(group.items).filter((i) => !i.apId && i.key !== 'prozesslandkarte');
    items.forEach((item, row) => {
      const text = PAGE_TEXTS[item.key] ?? { title: item.label ?? item.key, description: '' };
      nodes.push({
        key: item.key, ...text, group: group.key,
        x, y: PAD + HEAD_H + row * (NODE_H + ROW_GAP),
      });
    });
    maxRows = Math.max(maxRows, items.length);
  });
  return {
    nodes, columns,
    width: PAD * 2 + columns.length * NODE_W + (columns.length - 1) * COL_GAP,
    height: PAD * 2 + HEAD_H + maxRows * NODE_H + (maxRows - 1) * ROW_GAP,
  };
}
